import { differenceInDays, format } from 'date-fns';
import * as React from 'react';
import { LayoutAnimation, TouchableHighlight, View } from 'react-native';
import Button from 'design-system/Button';
import IconArrowCollapsed from 'design-system/icons/IconArrowCollapsed';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import ToolTip from 'design-system/ToolTip';
import { rem } from 'design-system/values';
import Status from 'design-system/Status';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import links from 'utils/links';
import { formatPlural } from 'utils';
import AccountsList from './AccountsList';
import BankNameRow from './BankNameRow';
import ConnectionUsedInAutosaveModal from './components/ConnectionUsedInAutosaveModal';
import { canRenewConnection, isConnectionBroken } from './utils';
import BankConnectionStatus from './components/BankConnectionStatus';
const strings = {
    renewConnection: 'Renew connection',
    getBackTo90Days: 'Get me back to 90 days',
    close: 'Close',
    activate: 'Activate',
    reopen: 'Reopen',
    delete: 'Delete',
    accountsImported: 'Accounts Imported',
    linkedFor: 'Bank connection',
    lastSynced: 'Last synced',
    whyRenew: 'Why do I need to renew?',
    infoTitle: 'Renew connection',
    infoSubtitle: 'To keep your accounts in sync, UK regulations require you to confirm every 90 days that you’re happy for Emma to access your bank data.',
    status: 'Status',
    autosave: 'Autosave',
};
const BankLoginCard = ({ data, onRemove, onClosed, onRenewAccess, onActivate, hideLinkedFor, hideCloseAndDelete, hideStatusMessage, hideButtons, headerRow, savingsPlanConnectionId, }) => {
    const navigation = useMainStackNavigation();
    const [expanded, setExpanded] = React.useState(false);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const navigateToAutosave = React.useCallback(() => {
        navigation.navigateDeprecated('AutoSave');
    }, []);
    const handleClose = React.useCallback(() => {
        if (savingsPlanConnectionId === data.id) {
            ConnectionUsedInAutosaveModal.show(navigateToAutosave, 'close');
            return;
        }
        onClosed?.(data);
    }, [data, navigateToAutosave, onClosed, savingsPlanConnectionId]);
    const handleDelete = React.useCallback(() => {
        if (savingsPlanConnectionId === data.id) {
            ConnectionUsedInAutosaveModal.show(navigateToAutosave, 'delete');
            return;
        }
        onRemove?.(data);
    }, [data, navigateToAutosave, onRemove, savingsPlanConnectionId]);
    const toggleSection = React.useCallback(() => {
        LayoutAnimation.configureNext({
            duration: 400,
            update: {
                duration: 200,
                delay: expanded ? 200 : 0,
                type: LayoutAnimation.Types.easeInEaseOut,
                property: LayoutAnimation.Properties.opacity,
            },
            create: {
                delay: expanded ? 0 : 200,
                duration: 200,
                type: LayoutAnimation.Types.easeInEaseOut,
                property: LayoutAnimation.Properties.opacity,
            },
            delete: {
                delay: expanded ? 200 : 0,
                duration: 200,
                type: LayoutAnimation.Types.easeInEaseOut,
                property: LayoutAnimation.Properties.opacity,
            },
        });
        setExpanded((value) => !value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleRenewAccess = React.useCallback(() => {
        onRenewAccess?.(data);
    }, [data, onRenewAccess]);
    const accountsRow = React.useMemo(() => {
        const numberOfAccounts = data.accounts.length;
        if (!numberOfAccounts)
            return null;
        const title = `${strings.accountsImported} (${numberOfAccounts})`;
        return (<>
        <TouchableHighlight underlayColor={colors.background.underlay} activeOpacity={1} onPress={toggleSection}>
          <View style={styles.rowInfo}>
            <Text TextThin-14 Secondary>
              {title}
            </Text>
            {expanded ? (<View key="expanded-icon" style={styles.expanded}>
                <IconArrowCollapsed color={colors.elements.secondary}/>
              </View>) : (<IconArrowCollapsed color={colors.elements.secondary}/>)}
          </View>
        </TouchableHighlight>
        {expanded ? <AccountsList accounts={data.accounts}/> : null}
      </>);
    }, [colors.background.underlay, colors.elements.secondary, data.accounts, expanded, styles, toggleSection]);
    const linkedForRow = React.useMemo(() => {
        if (!hideLinkedFor && data.consentExpiresAt && !data.isClosed) {
            if (data.needsReauth || data.needsReconsent)
                return null;
            const days = differenceInDays(new Date(data.consentExpiresAt), new Date());
            return (<View style={styles.rowInfo}>
          <Text TextThin-14 Secondary>
            {strings.linkedFor}
          </Text>
          <Text Text-14>
            {days} {formatPlural('day', 'days', days)} left
          </Text>
        </View>);
        }
        return null;
    }, [data.consentExpiresAt, data.isClosed, data.needsReauth, data.needsReconsent, hideLinkedFor, styles.rowInfo]);
    const lastSyncRow = React.useMemo(() => {
        if (data.lastSuccessfulSync && !data.isClosed) {
            return (<View style={styles.rowInfo}>
          <Text TextThin-14 Secondary>
            {strings.lastSynced}
          </Text>
          <Text Text-14>{format(new Date(data.lastSuccessfulSync), 'd MMM h:mm a')}</Text>
        </View>);
        }
        return null;
    }, [data.lastSuccessfulSync, data.isClosed, styles]);
    const autosaveRow = React.useMemo(() => {
        if (savingsPlanConnectionId === data.id) {
            return (<View style={styles.rowInfo}>
          <Text TextThin-14 Secondary>
            {strings.autosave}
          </Text>
          <View style={styles.rowCentered}>
            <Status activeText="Linked" inactiveText="Linked" active/>
          </View>
        </View>);
        }
        return null;
    }, [data.id, savingsPlanConnectionId, styles.rowCentered, styles.rowInfo]);
    const statusRow = React.useMemo(() => (<View style={styles.rowInfo}>
        <Text TextThin-14 Secondary>
          {strings.status}
        </Text>
        <BankConnectionStatus data={data}/>
      </View>), [data, styles.rowInfo]);
    const buttonsRow = React.useMemo(() => {
        if (hideButtons) {
            return null;
        }
        const { connectorRemoved } = data.bankInfo;
        const closeButtonTitle = data.isClosed ? strings.reopen : strings.close;
        const button = (() => {
            if (!data.isClosed && canRenewConnection(data)) {
                return (<Button brandReversed square medium title={strings.renewConnection} onPress={handleRenewAccess} preventDoubleTap/>);
            }
            return null;
        })();
        if (data.deactivatedOverQuota && !data.isClosed) {
            return (<View style={styles.buttonsContainer}>
          <View style={styles.row}>
            <Button gray flex medium title={strings.activate} onPress={() => {
                    onActivate?.(data);
                }}/>
            <Spacer w={8}/>
            <Button gray flex medium title={strings.delete} onPress={handleDelete}/>
          </View>
          <Spacer h={8}/>
        </View>);
        }
        return (<View style={styles.buttonsContainer}>
        {button !== null && (<>
            {button}
            <Spacer h={8}/>
          </>)}
        {hideCloseAndDelete ? null : (<View style={styles.row}>
            {!connectorRemoved && (<>
                <Button brandBorder square flex medium title={closeButtonTitle} onPress={handleClose}/>
                <Spacer w={8}/>
              </>)}
            <Button brandBorder square flex medium title={strings.delete} onPress={handleDelete}/>
          </View>)}

        {data.consentExpiresAt ? (<>
            <Spacer h={16}/>
            <ToolTip centered title={strings.infoTitle} subtitle={strings.infoSubtitle} message={strings.whyRenew} learnMore onPressLearnMore={links.openRenewConnectionHelp}/>
          </>) : null}
        <Spacer h={16}/>
      </View>);
    }, [
        hideButtons,
        data,
        styles.buttonsContainer,
        styles.row,
        hideCloseAndDelete,
        handleClose,
        handleDelete,
        handleRenewAccess,
        onActivate,
    ]);
    return (<View style={styles.container}>
      {headerRow || <BankNameRow data={data.bankInfo} style={styles.bankNameRow}/>}
      <View style={styles.separator}/>
      {accountsRow}
      {linkedForRow}
      {lastSyncRow}
      {statusRow}
      {autosaveRow}
      {!hideStatusMessage && !data.deactivatedOverQuota && data.statusMessage != null ? (<View>
          <Spacer.H8 />
          <Text TextThin-14 {...(isConnectionBroken(data) ? { Negative: true } : { Secondary: true })} style={styles.buttonsContainer}>
            {data.statusMessage}
          </Text>
        </View>) : null}
      {buttonsRow ? <Spacer h={16}/> : null}
      {buttonsRow}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginHorizontal: rem(16),
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    separator: {
        height: 1,
        backgroundColor: colors.background.dark,
    },
    buttonsContainer: {
        marginHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
    },
    bankNameRow: {
        padding: rem(16),
    },
    rowCentered: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    rowInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: rem(16),
    },
    expanded: {
        transform: [{ rotate: '180deg' }],
    },
}));
export default BankLoginCard;
